/**
 * Block-based email body builder: types and HTML generation (inline styles for email clients).
 */

export type TemplateBlock =
  | { id: string; type: 'paragraph'; text: string }
  | { id: string; type: 'list'; title?: string; items: string[] }
  | { id: string; type: 'button'; label: string; href: string }
  | { id: string; type: 'kv'; rows: { label: string; value: string }[] }
  | { id: string; type: 'divider' };

export type StarterBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; title?: string; items: string[] }
  | { type: 'button'; label: string; href: string }
  | { type: 'kv'; rows: { label: string; value: string }[] }
  | { type: 'divider' };

/** Escape HTML but preserve {{ placeholder }} tokens */
export function escapeHtmlPreservePlaceholders(text: string): string {
  return text
    .replace(/\{\{[^}]+\}\}/g, (match) => `\u0000${match}\u0000`)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\u0000(\{\{[^}]+\}\})\u0000/g, (_, p) => p);
}

export function blocksToHtml(blocks: TemplateBlock[], signature: string): string {
  const parts: string[] = [];

  for (const block of blocks) {
    switch (block.type) {
      case 'paragraph':
        parts.push(
          `<p style="margin:0 0 16px 0;font-size:16px;line-height:1.6;color:#334155;">${escapeHtmlPreservePlaceholders(block.text)}</p>`
        );
        break;
      case 'list': {
        const title = block.title
          ? `<p style="margin:0 0 8px 0;font-size:16px;font-weight:600;color:#1e293b;">${escapeHtmlPreservePlaceholders(block.title)}</p>`
          : '';
        const items = (block.items || [])
          .map(
            (item) =>
              `<li style="margin:4px 0;font-size:15px;line-height:1.5;color:#334155;">${escapeHtmlPreservePlaceholders(item)}</li>`
          )
          .join('');
        parts.push(
          `${title}<ul style="margin:0 0 16px 0;padding-left:24px;">${items}</ul>`
        );
        break;
      }
      case 'button':
        parts.push(
          `<div style="margin:24px 0;text-align:center;">
  <a href="${escapeHtmlPreservePlaceholders(block.href)}" style="background-color:#4f46e5;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:bold;display:inline-block;">${escapeHtmlPreservePlaceholders(block.label)}</a>
</div>`
        );
        break;
      case 'kv': {
        const rows = (block.rows || [])
          .map(
            (r) =>
              `<tr>
  <td style="padding:8px 12px 8px 0;font-size:14px;color:#64748b;vertical-align:top;border-bottom:1px solid #f1f5f9;">${escapeHtmlPreservePlaceholders(r.label)}</td>
  <td style="padding:8px 0;font-size:14px;color:#334155;border-bottom:1px solid #f1f5f9;">${escapeHtmlPreservePlaceholders(r.value)}</td>
</tr>`
          )
          .join('');
        parts.push(
          `<table style="width:100%;border-collapse:collapse;margin:0 0 16px 0;">${rows}</table>`
        );
        break;
      }
      case 'divider':
        parts.push(
          '<hr style="border:none;border-top:1px solid #e2e8f0;margin:24px 0;" />'
        );
        break;
    }
  }

  let body = parts.join('\n');
  if (signature.trim()) {
    const sigSafe = escapeHtmlPreservePlaceholders(signature).replace(/\n/g, '<br/>');
    body += '\n<div style="margin-top:32px;border-top:1px solid #f1f5f9;padding-top:16px;color:#64748b;font-size:14px;">' + sigSafe + '</div>';
  }
  return body;
}

/** Extract {{ placeholder }} names from a string */
export function extractPlaceholders(str: string): string[] {
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const set = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(str)) !== null) set.add(m[1]);
  return Array.from(set);
}

export const TEMPLATE_REGISTRY: Record<
  string,
  {
    allowed: string[];
    required: string[];
    sample: Record<string, string>;
    starter: {
      name: string;
      subject_template: string;
      signature: string;
      default_variables: Record<string, string>;
      blocks: StarterBlock[];
    };
  }
> = {
  invite_devteam: {
    allowed: ['invitee_name', 'inviter_name', 'product_name', 'access_level', 'invitee_email', 'invite_link', 'expires_at', 'support_email'],
    required: ['invitee_name', 'inviter_name', 'invite_link'],
    sample: {
      invitee_name: 'Kumar',
      inviter_name: 'Admin',
      product_name: 'Tamil Katral Maiyam',
      invite_link: '#',
      expires_at: '2026-02-10',
      access_level: 'DEV_ADMIN',
      invitee_email: 'kumar@example.com',
      support_email: 'support@example.com',
    },
    starter: {
      name: 'Invite DEV_TEAM (Global Default)',
      subject_template: 'Action Required: Join the {{ product_name }} Developer Team',
      signature: 'Best regards,<br/>{{ inviter_name }}',
      default_variables: { product_name: 'Tamil Katral Maiyam' },
      blocks: [
        { type: 'paragraph', text: 'Hello {{ invitee_name }},\n\nYou have been invited by {{ inviter_name }} to join the developer group for {{ product_name }}.' },
        { type: 'kv', rows: [
          { label: 'Role Level:', value: '{{ access_level }}' },
          { label: 'Registered Email:', value: '{{ invitee_email }}' },
        ]},
        { type: 'button', label: 'Complete Setup', href: '{{ invite_link }}' },
        { type: 'paragraph', text: 'This invitation expires on {{ expires_at }}. If you have technical issues, reach out to {{ support_email }}.' },
      ],
    },
  },
  invite_school_admin: {
    allowed: ['product_name', 'invitee_name', 'inviter_name', 'school_name', 'school_subdomain', 'invite_link', 'timezone', 'expires_at'],
    required: ['invitee_name', 'school_name', 'invite_link'],
    sample: {
      invitee_name: 'Siva',
      inviter_name: 'Admin',
      product_name: 'Tamil Katral Maiyam',
      school_name: 'ABC Tamil School',
      school_subdomain: 'abc.tkm.ai',
      invite_link: '#',
      timezone: 'Asia/Kolkata',
      expires_at: '2026-02-10',
    },
    starter: {
      name: 'Invite SCHOOL_ADMIN (Global Default)',
      subject_template: 'Action Required: Set up your {{ product_name }} School Admin account for {{ school_name }}',
      signature: 'Best regards,<br/>{{ inviter_name }}',
      default_variables: { product_name: 'Tamil Katral Maiyam' },
      blocks: [
        { type: 'paragraph', text: 'Welcome {{ invitee_name }},\n\n{{ inviter_name }} has assigned you as an Administrator for {{ school_name }}.' },
        { type: 'paragraph', text: 'Your school dashboard is located at: {{ school_subdomain }}.' },
        { type: 'button', label: 'Access Admin Console', href: '{{ invite_link }}' },
        { type: 'divider' },
        { type: 'kv', rows: [
          { label: 'Timezone:', value: '{{ timezone }}' },
          { label: 'Expiry:', value: '{{ expires_at }}' },
        ]},
      ],
    },
  },
  invite_teacher: {
    allowed: ['product_name', 'teacher_name', 'inviter_name', 'school_name', 'invite_link', 'expires_at'],
    required: ['teacher_name', 'school_name', 'invite_link'],
    sample: {
      teacher_name: 'Priya',
      inviter_name: 'Admin',
      product_name: 'Tamil Katral Maiyam',
      school_name: 'ABC Tamil School',
      invite_link: '#',
      expires_at: '2026-02-10',
    },
    starter: {
      name: 'Invite TEACHER (Tenant Default)',
      subject_template: 'Action Required: Join {{ product_name }} as a Teacher for {{ school_name }}',
      signature: 'Best regards,<br/>{{ inviter_name }}',
      default_variables: { product_name: 'Tamil Katral Maiyam' },
      blocks: [
        { type: 'paragraph', text: 'Hello {{ teacher_name }},\n\n{{ inviter_name }} has invited you to join {{ school_name }} as a Teacher.' },
        { type: 'button', label: 'Accept Invitation', href: '{{ invite_link }}' },
        { type: 'paragraph', text: 'This invitation expires on {{ expires_at }}.' },
      ],
    },
  },
  invite_student: {
    allowed: ['product_name', 'student_name', 'school_name', 'inviter_name', 'invite_link', 'expires_at'],
    required: ['student_name', 'school_name', 'invite_link'],
    sample: {
      student_name: 'Arun',
      inviter_name: 'Admin',
      product_name: 'Tamil Katral Maiyam',
      school_name: 'ABC Tamil School',
      invite_link: '#',
      expires_at: '2026-02-10',
    },
    starter: {
      name: 'Invite STUDENT (Tenant Default)',
      subject_template: 'Welcome to {{ product_name }} — join {{ school_name }} as a Student',
      signature: 'Best regards,<br/>{{ inviter_name }}',
      default_variables: { product_name: 'Tamil Katral Maiyam' },
      blocks: [
        { type: 'paragraph', text: 'Hi {{ student_name }},\n\nWelcome to {{ school_name }}! {{ inviter_name }} has created your student profile.' },
        { type: 'button', label: 'Enter Portal', href: '{{ invite_link }}' },
        { type: 'paragraph', text: 'Link valid until {{ expires_at }}.' },
      ],
    },
  },
  invite_parent: {
    allowed: ['product_name', 'invitee_name', 'school_name', 'student_name', 'invite_link', 'expires_at', 'inviter_name'],
    required: ['invitee_name', 'student_name', 'school_name', 'invite_link'],
    sample: {
      invitee_name: 'Ravi',
      student_name: 'Arun',
      inviter_name: 'Admin',
      product_name: 'Tamil Katral Maiyam',
      school_name: 'ABC Tamil School',
      invite_link: '#',
      expires_at: '2026-02-10',
    },
    starter: {
      name: 'Invite PARENT (Tenant Default)',
      subject_template: 'Action Required: Join {{ product_name }} as a Parent for {{ school_name }}',
      signature: 'Best regards,<br/>{{ inviter_name }}',
      default_variables: { product_name: 'Tamil Katral Maiyam' },
      blocks: [
        { type: 'paragraph', text: 'Dear {{ invitee_name }},\n\nYou are invited to join {{ school_name }} for your child, {{ student_name }}.' },
        { type: 'button', label: 'Connect to Portal', href: '{{ invite_link }}' },
        { type: 'paragraph', text: 'Expires: {{ expires_at }}.' },
      ],
    },
  },
};

export const TEMPLATE_KEYS = Object.keys(TEMPLATE_REGISTRY);
